<template>
  <!-- One downloadable mark on a brand page: the asset on a tile sized for it,
       then its name, its file type and the two things you can do with it. -->
  <BaseCard as="figure" variant="tile" padding="none" interactive class="flex h-full flex-col overflow-hidden">
    <!-- Same white/dark decision the hero and the brand cards make: roughly a
         fifth of these assets are the light-ink variant and would be an empty
         box on white. The tone is precomputed per asset by
         scripts/detect-logo-tone.mjs; the mid grey is for the handful whose ink
         it can't read, where either extreme risks hiding the mark entirely. -->
    <div class="flex h-28 items-center justify-center px-5" :class="tile">
      <img
        :src="logo.url"
        :alt="alt"
        loading="lazy"
        decoding="async"
        class="max-h-14 max-w-full object-contain"
      />
    </div>

    <!-- The name and the actions stack rather than sitting side by side: these
         cards are ~160px wide on a phone, which is not enough for a label and
         two labelled buttons on one line. -->
    <figcaption class="flex flex-1 flex-col justify-between gap-1.5 px-3 py-2.5">
      <div class="flex items-baseline gap-1.5">
        <p class="truncate text-xs font-semibold text-white">{{ logo.label }}</p>
        <p class="shrink-0 text-2xs uppercase tracking-wide text-gray-500">{{ logo.format }}</p>
      </div>

      <!-- Pulled left by the buttons' own padding, so "Copy" lines up with the
           label above it. They wrap rather than overflow on the narrowest cards. -->
      <div class="-ml-2 flex flex-wrap items-center gap-y-1">
        <!-- The same copy glyph the swatch cards use, so the two Copy buttons on
             a brand page read as the same action. -->
        <GhostIconButton :aria-label="`Copy ${alt}`" @click="$emit('copy', logo)">
          <template #icon>
            <CopyIcon class="size-3.5" />
          </template>
          <span class="text-xs">Copy</span>
        </GhostIconButton>

        <GhostIconButton :aria-label="`Save ${logo.filename}`" @click="$emit('download', logo)">
          <template #icon>
            <ArrowDownTrayIcon class="size-4" aria-hidden="true" />
          </template>
          <span class="text-xs">Save</span>
        </GhostIconButton>
      </div>
    </figcaption>
  </BaseCard>
</template>

<script setup>
import { computed } from 'vue'
import { ArrowDownTrayIcon } from '@heroicons/vue/16/solid'
import BaseCard from '~/components/atoms/BaseCard.vue'
import CopyIcon from '~/components/atoms/CopyIcon.vue'
import GhostIconButton from '~/components/atoms/GhostIconButton.vue'

const props = defineProps({
  // A normalised asset from server/utils/brands.ts:
  // { url, label, format, tone, filename }
  logo: { type: Object, required: true },
  brandName: { type: String, required: true },
})

defineEmits(['copy', 'download'])

const TILES = {
  light: 'bg-gray-800',
  dark: 'bg-white',
  unknown: 'bg-gray-500',
}

const tile = computed(() => TILES[props.logo.tone] ?? TILES.dark)

/** "Alternate" alone doesn't describe an image; the rest already do. */
const ALT_NOUNS = {
  'Primary logo': 'logo',
  Logo: 'logo',
  Symbol: 'symbol',
  Icon: 'icon',
  Alternate: 'alternate logo',
}

const alt = computed(() => `${props.brandName} ${ALT_NOUNS[props.logo.label] ?? 'logo'}`)
</script>
